import {
    useEffect, useMemo, useState,
} from 'react'
import omit from 'lodash/omit'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'

import {
    OrderTransportModeRoad, OrderModificationType,
    PositioningSelection,
} from 'app/enums/label.enums'
import {
    Order,
} from '../../../orders.types'
import {
    PositioningSetupType,
} from '../orderConfirmation.types'
import getServiceCenterMap from '../getServiceCenterMap'
import useUpdatePositioningSetup from '../hooks/useUpdatePositioningSetup'
import getFields from './positioningSetup.fields'
import useStyles from './PositioningSetup.style'

const PositioningSetup = ({
    data,
    order,
    isDeliveryRoad,
    onSuccess,
}: {
    data: PositioningSetupType,
    order: Order,
    isDeliveryRoad: boolean,
    onSuccess: () => void
}) => {
    const getAllowedValues = useEnumValues()
    const serviceCenterMap = useMemo(() => {
        return getServiceCenterMap(data)
    }, [data])
    const {
        classes,
    } = useStyles()
    const [
        value,
        setValue,
    ] = useState(data)

    const {
        forwarderPositioningSelection,
        transportModeForwarder,
        forwarderPos,
        leaseType,
        deliveryOptionRoad,
    } = value

    const [defaultValue] = useState(data)

    const [
        modification,
        setModification,
    ] = useState<OrderModificationType>()

    useEffect(() => {
        setValue((prevState) => {
            return {
                ...prevState,
                ...serviceCenterMap.forwarder[forwarderPositioningSelection].data,
            }
        })
    }, [
        forwarderPositioningSelection,
        serviceCenterMap.forwarder,
    ])

    const {
        mutate,
        isPending: isLoading,
    } = useUpdatePositioningSetup(onSuccess)

    const resetData = (): void => {
        setValue(defaultValue)
    }

    const fields = useMemo(() => {
        return getFields({
            order,
            transportModeForwarder,
            getAllowedValues,
            isDeliveryRoad,
            forwarderPositioningSelection,
            leaseType,
            deliveryOptionRoad,
            forwarderPositioningId: forwarderPos?.id,
            isOtherByDefault: data.forwarderPositioningSelection
                === PositioningSelection.OTHER,
        })
    }, [
        order,
        transportModeForwarder,
        getAllowedValues,
        isDeliveryRoad,
        forwarderPositioningSelection,
        leaseType,
        deliveryOptionRoad,
        forwarderPos?.id,
        data.forwarderPositioningSelection,
    ])

    const fieldsWithClassName = useFieldsWithClassName(fields, classes)

    const isSaveDisabled = defaultValue === value

    const update = (modificationType: OrderModificationType) => {
        mutate({
            modificationType,
            orderNumber: order?.orderNumber,
            data: transportModeForwarder === OrderTransportModeRoad.FTL ? value : omit(value, [
                'deliveryOptionRoad',
                'numberOfTrucks',
                'numberOfVans',
            ]),
        })
        setModification(modificationType)
    }

    return (
        <>
            <DomainObjectForm
                value={value}
                onChange={setValue}
                fields={fieldsWithClassName}
                name="OrderByNumberView"
                className={classes.gridWrapper}
            />
            <ButtonContainer>
                <Button
                    label="Save (SkyCell)"
                    onClick={() => {
                        update(OrderModificationType.INTERNAL)
                    }}
                    data-testid="save-btn-internal"
                    disabled={
                        isSaveDisabled || isLoading
                    }
                    saving={isLoading && modification === OrderModificationType.INTERNAL}
                />
                <Button
                    label="Save (Customer)"
                    onClick={() => {
                        update(OrderModificationType.CUSTOMER)
                    }}
                    disabled={isSaveDisabled || isLoading}
                    data-testid="save-btn-customer"
                    saving={isLoading && modification === OrderModificationType.CUSTOMER}
                />
                <Button
                    secondary
                    label="Cancel"
                    onClick={resetData}
                    disabled={isLoading}
                    data-testid="cancel-btn"
                />
            </ButtonContainer>
        </>
    )
}

export default PositioningSetup
